export type PasswordRequirementKey = "length" | "uppercase" | "lowercase" | "number";

export type PasswordRequirement = {
  key: PasswordRequirementKey;
  label: string;
  met: boolean;
};

export type PasswordStrength = {
  score: number;
  label: "Too weak" | "Basic" | "Good" | "Strong";
  policyValid: boolean;
  requirements: PasswordRequirement[];
};

export function evaluatePasswordStrength(password: string): PasswordStrength {
  const requirements: PasswordRequirement[] = [
    {
      key: "length",
      label: "At least 12 characters",
      met: password.length >= 12,
    },
    {
      key: "uppercase",
      label: "Uppercase letter",
      met: /[A-Z]/.test(password),
    },
    {
      key: "lowercase",
      label: "Lowercase letter",
      met: /[a-z]/.test(password),
    },
    {
      key: "number",
      label: "Number",
      met: /\d/.test(password),
    },
  ];
  const metCount = requirements.filter((requirement) => requirement.met).length;
  const bonus =
    (password.length >= 16 ? 1 : 0) +
    (/[^\dA-Za-z]/.test(password) && password.length >= 12 ? 1 : 0);
  const score = Math.min(4, metCount + bonus);
  const policyValid = requirements.every((requirement) => requirement.met);

  if (!password) {
    return {
      score: 0,
      label: "Too weak",
      policyValid: false,
      requirements,
    };
  }

  return {
    score,
    label:
      score >= 4 && policyValid
        ? "Strong"
        : policyValid
          ? "Good"
          : score >= 3
            ? "Basic"
            : "Too weak",
    policyValid,
    requirements,
  };
}
